import {useEffect, useState, useRef} from "react"
import {paymentArrangementAPI} from "@indebted/api/paymentArrangementAPI"
import {LoadingPage} from "@indebted/components/Loading"
import {ErrorPage} from "@indebted/components/Error"
import {useLocation} from "react-router-dom"

import {InstalmentStatusResponse} from "../types"
import {usePaymentArrangementId} from "../usePaymentArrangementId"

import {PaymentPlanInProgressPage} from "./PaymentPlanInProgress"
import {PaymentSuccessfulPage} from "./PaymentSuccessful"
import {PaymentFailedPage} from "./PaymentFailed"

function InstalmentStatus() {
	const paymentArrangementID = usePaymentArrangementId()
	const location = useLocation()
	const i18n = location?.state?.I18n
	const [Component, setComponent] = useState(<LoadingPage I18n={i18n} />)
	const abortControllerRef = useRef<AbortController | null>(null)

	const instalmentStatusComponent = (status: InstalmentStatusResponse["Status"]) => {
		const map = {
			initiated: PaymentPlanInProgressPage,
			submitted: PaymentPlanInProgressPage,
			succeeded: PaymentSuccessfulPage,
			failed: PaymentFailedPage,
		} as const
		return map[status]
	}

	useEffect(() => {
		abortControllerRef.current = new AbortController()

		paymentArrangementAPI
			.pollInstalmentConfirmation(paymentArrangementID, abortControllerRef.current.signal)
			.then((result) => {
				const typedResult = result as InstalmentStatusResponse
				const Component = instalmentStatusComponent(typedResult.Status)
				setComponent(<Component paymentArrangementID={paymentArrangementID} {...typedResult} />)
			})
			.catch((error: Error) => {
				if (error.message === "AbortError" || error.message === "Polling aborted") return
				setComponent(<ErrorPage error={error} />)
			})
	}, [paymentArrangementID])

	useEffect(() => {
		// Only abort on unmount (when user leaves the page)
		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort()
			}
		}
	}, [])

	return <div data-testid="payment-arrangement-instalment-status">{Component}</div>
}

export {InstalmentStatus}
